import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "./api";
import { PageHeader, TextInput, TextArea, Button, Card, Toggle, EmptyState } from "./ui";
import { Save, Plus, Trash2, Pencil, X } from "lucide-react";

const ICON_OPTIONS = ["Layers", "Droplets", "Zap", "Settings", "Building2", "Boxes", "Cpu", "Ruler", "Users", "Package"];

export default function AdminServices() {
  return <CRUDPage endpoint="services" title="Hizmetler" Form={ServiceForm} columns={[
    { k: "title", l: "Başlık" }, { k: "key", l: "Anahtar" }, { k: "icon", l: "İkon" },
  ]} emptyModel={{ key: "", title: "", desc: "", icon: "Layers", order: 0, active: true }} testid="services" />;
}

function ServiceForm({ v, set }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput label="Anahtar (key)" value={v.key || ""} onChange={(e) => set("key", e.target.value)} testid="f-key" />
        <TextInput label="Başlık (varsayılan / TR)" value={v.title || ""} onChange={(e) => set("title", e.target.value)} testid="f-title" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput label="Başlık TR (üzerine yazar, opsiyonel)" value={v.title_tr || ""} onChange={(e) => set("title_tr", e.target.value)} />
        <TextInput label="Başlık EN" value={v.title_en || ""} onChange={(e) => set("title_en", e.target.value)} />
      </div>
      <TextArea label="Açıklama (varsayılan / TR)" rows={3} value={v.desc || ""} onChange={(e) => set("desc", e.target.value)} testid="f-desc" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextArea label="Açıklama TR (opsiyonel)" rows={3} value={v.description_tr || ""} onChange={(e) => set("description_tr", e.target.value)} />
        <TextArea label="Açıklama EN" rows={3} value={v.description_en || ""} onChange={(e) => set("description_en", e.target.value)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block">İkon</span>
          <select value={v.icon || "Layers"} onChange={(e) => set("icon", e.target.value)} className="w-full bg-[#050505] border border-white/10 text-white font-mono text-sm h-11 px-3 focus:border-[#FF4F00] focus:outline-none">
            {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </label>
        <TextInput label="Sıra" type="number" value={v.order ?? 0} onChange={(e) => set("order", parseInt(e.target.value) || 0)} />
        <div className="flex items-end"><Toggle checked={!!v.active} onChange={(x) => set("active", x)} label={v.active ? "Aktif" : "Pasif"} /></div>
      </div>
    </div>
  );
}

// Generic CRUD page
export function CRUDPage({ endpoint, title, Form, columns, emptyModel, testid, extraCols, customRender }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null); // item being edited, or 'new'

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/${endpoint}`); setItems(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async (v) => {
    try {
      if (v.id) { await api.put(`/admin/${endpoint}/${v.id}`, v); toast.success("Güncellendi"); }
      else { await api.post(`/admin/${endpoint}`, v); toast.success("Eklendi"); }
      setOpen(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try { await api.delete(`/admin/${endpoint}/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader
        title={title}
        right={<Button onClick={() => setOpen(emptyModel)} data-testid={`${testid}-add`}><Plus className="w-4 h-4" /> Yeni</Button>}
      />

      {items.length === 0 ? (
        <EmptyState />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${testid}-table`}>
              <thead className="bg-[#050505] border-b border-white/10">
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <th className="p-4">Sıra</th>
                  {columns.map((c) => <th key={c.k} className="p-4">{c.l}</th>)}
                  <th className="p-4">Durum</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 font-mono text-xs text-zinc-500">{it.order ?? 0}</td>
                    {columns.map((c) => (
                      <td key={c.k} className="p-4">
                        {customRender?.(c.k, it) || <span className="text-zinc-200">{String(it[c.k] ?? "—")}</span>}
                      </td>
                    ))}
                    <td className="p-4">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${it.active ? "text-[#00FF9D] border-[#00FF9D]/40" : "text-zinc-500 border-white/10"}`}>
                        {it.active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td className="p-4 flex gap-2">
                      <button onClick={() => setOpen(it)} data-testid={`${testid}-edit-${it.id}`} className="text-zinc-400 hover:text-[#FF4F00]"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(it.id)} data-testid={`${testid}-delete-${it.id}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {open && (
        <EditModal item={open} onClose={() => setOpen(null)} onSave={save} Form={Form} testid={testid} />
      )}
    </div>
  );
}

function EditModal({ item, onClose, onSave, Form, testid }) {
  const [v, setV] = useState(item);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 grid place-items-center p-6" onClick={onClose}>
      <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10 bg-[#0C0C0F] p-8" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-form`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl font-black uppercase tracking-tight">{v.id ? "Düzenle" : "Yeni"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <Form v={v} set={set} />
        <div className="flex gap-3 mt-8 pt-6 border-t border-white/10">
          <Button onClick={() => onSave(v)} data-testid={`${testid}-save`}><Save className="w-4 h-4" /> Kaydet</Button>
          <Button variant="secondary" onClick={onClose}>İptal</Button>
        </div>
      </div>
    </div>
  );
}
